import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { buildInstallUrl } from "../services/AttributionService";
import { track } from "../utils/analytics";

const STORAGE_KEY = "ios_modal_dismissed_at";
/** Dismiss is hidden for ~1 month, then the prompt can show again */
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

const shouldShow = () => {
  if (!isIos()) return false;
  const dismissedAt = localStorage.getItem(STORAGE_KEY);
  if (!dismissedAt) return true;
  const ts = parseInt(dismissedAt, 10);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > ONE_MONTH_MS;
};

export default function IosAppModal() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(shouldShow());
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 dark:bg-black/70 backdrop-blur-sm" onClick={dismiss}>
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200/80 dark:border-dark-700 bg-white dark:bg-dark-900 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 text-center relative">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-dark-400 dark:hover:bg-dark-800 dark:hover:text-dark-100 transition-colors"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <img src="/logo-240.png" alt="DealHawk" className="h-10 mx-auto mb-4" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-dark-50">
            Get the DealHawk App
          </h3>
          <p className="mb-6 text-sm leading-relaxed text-gray-500 dark:text-dark-300">
            Download our app from the App Store for push notifications, faster browsing, and more.
          </p>
          <a
            href={buildInstallUrl("ios")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track("app_store_badge_clicked", { platform: "ios", location: "ios_app_modal" });
              dismiss();
            }}
            className="mb-3 inline-flex w-full items-center justify-center gap-3 rounded-xl bg-black pl-4 pr-5 py-3 text-white transition-all hover:bg-gray-800 dark:bg-black dark:text-white dark:hover:bg-gray-900"
          >
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <div className="flex flex-col items-start leading-tight">
              <span className="text-[10px] uppercase tracking-wider opacity-80">Download on the</span>
              <span className="text-lg font-semibold -mt-0.5">App Store</span>
            </div>
          </a>
          <button
            onClick={dismiss}
            className="w-full rounded-xl py-2.5 font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-dark-300 dark:hover:bg-dark-800 dark:hover:text-dark-100"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
